<script setup lang="ts">
// #750: 公開作品の「つくった人たち」= Lineage の "作り手(authored)" projection。
// GET /api/v1/public/works/{slug}/credits は public（未ログインでも見える）ので
// useWorkApi().getWorkCredits（publicFetch）で取得する。読込中・記録なしも必ず描画して
// 「クリックしたのに無反応」を避ける（隠しドットからのインライン表示）。
//
// #835: **著作表記が必須の素材（魔王魂等）を含む作品では、隠せない。** 親から
// `revealed` を受け取るが、`attributionRequired` が立っていれば無視して常に出す。
// 判定にはレスポンスが要るので、取得自体は revealed に関係なく行う。
import { mdiAccountGroup, mdiOpenInNew } from '@mdi/js'
import { useWorkApi } from '~/composables/api/useWorkApi'
import type { CreditLine, CreditPerson } from '~/composables/api/useWorkApi'

interface Props {
  slug: string
  /** 親が「見せてよい」と判断しているか。#837 で常時 true になる予定。 */
  revealed?: boolean
}

const props = withDefaults(defineProps<Props>(), { revealed: true })

const { getWorkCredits } = useWorkApi()

const lines = ref<CreditLine[]>([])
const loading = ref(false)
const attributionRequired = ref(false)

const load = async () => {
  if (!props.slug) {
    lines.value = []
    return
  }
  loading.value = true
  try {
    const res = await getWorkCredits(props.slug)
    lines.value = res.lines ?? []
    attributionRequired.value = res.attributionRequired ?? false
  }
  catch (e) {
    console.error('[WorkCredits] getWorkCredits failed:', e)
    lines.value = []
    attributionRequired.value = false
  }
  finally {
    loading.value = false
  }
}

onMounted(load)
watch(() => props.slug, load)

// #836: 「自由に使ってよい素材ですが〜」の注記は撤去した。ライセンスは本文では
// なくバッジ（`LicenseBadge`）で示すので、同じことを文章で繰り返す必要が無い。

// #835: 著作表記が義務の素材があれば、親が隠そうとしても表示する。
const visible = computed(() => props.revealed || attributionRequired.value)

const personKey = (person: CreditPerson): string => person.slug ?? person.name
</script>

<template>
  <section
    v-if="visible"
    class="work-credits"
  >
    <h3 class="work-credits__title">
      <v-icon
        :icon="mdiAccountGroup"
        size="small"
        aria-hidden="true"
      />
      <span>つくった人たち</span>
    </h3>

    <p
      v-if="loading"
      class="work-credits__empty"
    >
      読み込み中…
    </p>

    <template v-else-if="lines.length">
      <ul class="work-credits__list">
        <li
          v-for="(line, idx) in lines"
          :key="idx"
          class="work-credits__row"
        >
          <div class="work-credits__main">
            <span class="work-credits__label">{{ line.label }}</span>
            <span class="work-credits__creator">
              <NuxtLink
                v-if="line.creator.slug"
                :to="`/c/${line.creator.slug}`"
                class="work-credits__link"
              >{{ line.creator.name }}</NuxtLink>
              <template v-else>{{ line.creator.name }}</template>
              <template v-if="line.editors.length">
                <span>（原作）</span>
                <template
                  v-for="editor in line.editors"
                  :key="personKey(editor)"
                >
                  <span>・</span>
                  <NuxtLink
                    v-if="editor.slug"
                    :to="`/c/${editor.slug}`"
                    class="work-credits__link"
                  >{{ editor.name }}</NuxtLink>
                  <template v-else>{{ editor.name }}</template>
                  <span>（改造）</span>
                </template>
              </template>
            </span>
          </div>
          <div class="work-credits__meta">
            <a
              v-if="line.sourceUrl"
              :href="line.sourceUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="work-credits__source"
            >出典
              <v-icon
                :icon="mdiOpenInNew"
                size="x-small"
                aria-hidden="true"
              /></a>
            <LicenseBadge
              :badge="line.licenseBadge"
              :tone="line.licenseTone"
              :url="line.licenseUrl"
              :note="line.licenseNote"
            />
          </div>
        </li>
      </ul>
    </template>

    <p
      v-else
      class="work-credits__empty"
    >
      この作品には、まだ作り手の記録がありません。
    </p>
  </section>
</template>

<style scoped>
.work-credits {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid rgba(201, 164, 64, 0.5);
}

.work-credits__title {
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: "Hiragino Mincho ProN", "Yu Mincho", "Noto Serif JP", "Noto Serif", serif;
  font-size: 0.9rem;
  font-weight: 700;
  color: #5b3a1a;
  margin: 0 0 8px;
}

.work-credits__empty {
  margin: 4px 0 0;
  font-size: 0.8rem;
  color: rgba(60, 40, 20, 0.55);
}

.work-credits__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.work-credits__row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 2px 10px;
  font-size: 0.85rem;
}

.work-credits__main {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px;
  min-width: 0;
}

.work-credits__label {
  color: rgba(60, 40, 20, 0.7);
  min-width: 4.5em;
}

.work-credits__creator {
  color: #2a1f10;
  font-weight: 600;
  word-break: break-word;
}

.work-credits__link {
  color: #1f3554;
  text-decoration: none;
  border-bottom: 1px dotted rgba(31, 53, 84, 0.4);
}

.work-credits__link:hover {
  border-bottom-color: #1f3554;
}

.work-credits__meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.75rem;
  color: rgba(60, 40, 20, 0.6);
  white-space: nowrap;
}

.work-credits__source {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  color: inherit;
  text-decoration: none;
  border-bottom: 1px dotted rgba(60, 40, 20, 0.4);
}

.work-credits__source:hover {
  color: #1f3554;
  border-bottom-color: #1f3554;
}
</style>
